import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/ui-components/ui/input";
import { Textarea } from "@/ui-components/ui/textarea";
import { Coins, Image as ImageIcon, MapPin, Building2, Link as LinkIcon, StickyNote, User } from "lucide-react";
import { toast } from "@/lib/toast";
import {
  useSponsorsApplyImageEdit,
  useSponsorsDeleteImage,
  useSponsorsPreviewBackgroundRemoval,
  useSponsorsRestoreOriginalImage,
  useSponsorsUpdate,
  useSponsorsUploadImage,
} from "@/lib/api/requests/sponsors.requests";
import {
  SponsorDetailsResponseDto,
  UpdateSponsorRequestDto,
} from "@/lib/api/interfaces/sponsors.interface";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { DetailsBaseDialog, DialogFormSection } from "@/util-components/dialog/base-dialogs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { ManagedImageBusyAction, ManagedImageField, persistManagedImageDraft } from "@/util-components/media";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

interface EditSponsorDialogProps {
  sponsor: SponsorDetailsResponseDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const updateSponsorSchema = z.object({
  name: z.string().min(1, { message: "Name ist erforderlich" }),
  website: z.string().optional(),
  address: z.string().optional(),
  street: z.string().optional(),
  streetNumber: z.string().optional(),
  zip: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  notes: z.string().optional(),
  assignedUserId: z.string().optional(),
  logo: z.instanceof(File).optional(),
});

type UpdateSponsorFormValues = z.infer<typeof updateSponsorSchema>;

/**
 * Edits sponsor fields while keeping image lifecycle operations on the shared
 * managed image flow.
 */
export const EditSponsorDialog: React.FC<EditSponsorDialogProps> = ({
  sponsor,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onOpenChange}
      mode="edit"
      icon={Coins}
      title="Sponsor bearbeiten"
      description="Hier kannst du die Stammdaten des Sponsors anpassen."
      hideDelete={true}
      hideEdit={true}
      isSaving={isSaving}
      canSave={canSave}
      formId="edit-sponsor-form"
      detailsContent={<></>}
      editContent={() => (
        <EditSponsorForm
          sponsor={sponsor}
          onOpenChange={onOpenChange}
          onSuccess={onSuccess}
          setIsSaving={setIsSaving}
          setCanSave={setCanSave}
        />
      )}
    />
  );
};

const EditSponsorForm = ({
  sponsor,
  onOpenChange,
  onSuccess,
  setIsSaving,
  setCanSave,
}: {
  sponsor: SponsorDetailsResponseDto;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const NONE_USER_VALUE = "NONE";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaBusyAction, setMediaBusyAction] = useState<ManagedImageBusyAction>(null);

  const updateSponsorMutation = useSponsorsUpdate();
  const { mutateAsync: uploadLogo } = useSponsorsUploadImage();
  const { mutateAsync: deleteLogo } = useSponsorsDeleteImage();
  const { mutateAsync: applyImageEdit } = useSponsorsApplyImageEdit();
  const { mutateAsync: restoreOriginalImage } = useSponsorsRestoreOriginalImage();
  const { mutateAsync: previewBackgroundRemoval } = useSponsorsPreviewBackgroundRemoval();

  const users = sponsor.selectableEntities?.users ?? [];

  const form = useForm<UpdateSponsorFormValues>({
    resolver: zodResolver(updateSponsorSchema),
    mode: "onChange",
    defaultValues: {
      name: sponsor.name || "",
      website: sponsor.website || "",
      address: sponsor.address || "",
      street: sponsor.street || "",
      streetNumber: sponsor.streetNumber || "",
      zip: sponsor.zip || "",
      city: sponsor.city || "",
      country: sponsor.country || "",
      notes: sponsor.notes || "",
      assignedUserId: sponsor.assignedUser?._id ?? NONE_USER_VALUE,
      logo: undefined,
    },
  });

  const watchedValues = form.watch();
  const logoFile = watchedValues.logo;
  const isLoading = isSubmitting || mediaBusyAction !== null;

  useEffect(() => {
    setIsSaving(isLoading);
  }, [isLoading, setIsSaving]);

  const hasChanges = useMemo(
    () =>
      watchedValues.name.trim() !== (sponsor.name ?? "") ||
      (watchedValues.website ?? "").trim() !== (sponsor.website ?? "") ||
      (watchedValues.address ?? "").trim() !== (sponsor.address ?? "") ||
      (watchedValues.street ?? "").trim() !== (sponsor.street ?? "") ||
      (watchedValues.streetNumber ?? "").trim() !== (sponsor.streetNumber ?? "") ||
      (watchedValues.zip ?? "").trim() !== (sponsor.zip ?? "") ||
      (watchedValues.city ?? "").trim() !== (sponsor.city ?? "") ||
      (watchedValues.country ?? "").trim() !== (sponsor.country ?? "") ||
      (watchedValues.notes ?? "").trim() !== (sponsor.notes ?? "") ||
      (watchedValues.assignedUserId === NONE_USER_VALUE ? undefined : watchedValues.assignedUserId) !== (sponsor.assignedUser?._id ?? undefined) ||
      !!logoFile,
    [watchedValues, sponsor, logoFile]
  );

  useEffect(() => {
    setCanSave(form.formState.isValid && hasChanges && mediaBusyAction === null);
  }, [form.formState.isValid, hasChanges, mediaBusyAction, setCanSave]);

  const runMediaAction = async ({
    action,
    task,
    successMessage,
    errorMessage,
  }: {
    action: Exclude<ManagedImageBusyAction, null>;
    task: () => Promise<unknown>;
    successMessage: string;
    errorMessage: string;
  }) => {
    setMediaBusyAction(action);

    try {
      await task();
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage, { error });
    } finally {
      setMediaBusyAction(null);
    }
  };

  const onSubmit = async (values: UpdateSponsorFormValues) => {
    const payload: UpdateSponsorRequestDto = {
      name: values.name.trim(),
      website: values.website?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      street: values.street?.trim() || undefined,
      streetNumber: values.streetNumber?.trim() || undefined,
      zip: values.zip?.trim() || undefined,
      city: values.city?.trim() || undefined,
      country: values.country?.trim() || undefined,
      assignedUserId: !values.assignedUserId || values.assignedUserId === NONE_USER_VALUE ? undefined : values.assignedUserId,
    };

    setIsSubmitting(true);

    try {
      await updateSponsorMutation.mutateAsync({ id: sponsor._id, data: payload as any });

      if (values.logo) {
        await persistManagedImageDraft({
          entityId: sponsor._id,
          file: values.logo,
          uploadImage: uploadLogo,
          applyImageEdit,
        });
      }

      toast.success("Sponsor erfolgreich aktualisiert", {
        duration: 3000,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update sponsor", error);
      toast.error("Fehler beim Speichern", {
        description: "Der Sponsor konnte nicht aktualisiert werden. Bitte versuche es erneut.",
        duration: 5000,
        error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form id="edit-sponsor-form" onSubmit={form.handleSubmit(onSubmit)} className="flex h-full flex-col overflow-y-auto px-5 py-4 sm:px-6 space-y-4 sm:space-y-6">
        <DialogFormSection title="Sponsorenlogo" icon={ImageIcon}>
          <ManagedImageField
            value={sponsor.logoState}
            file={logoFile}
            onFileChange={(nextFile) => form.setValue("logo", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
            onDeleteRemote={() =>
              runMediaAction({
                action: "delete",
                task: () => deleteLogo({ id: sponsor._id }),
                successMessage: "Logo wurde entfernt",
                errorMessage: "Logo konnte nicht entfernt werden",
              })
            }
            onApplyEdit={(edit) =>
              runMediaAction({
                action: "applyEdit",
                task: () => applyImageEdit({ id: sponsor._id, data: edit }),
                successMessage: "Bildbearbeitung wurde übernommen",
                errorMessage: "Logo konnte nicht bearbeitet werden",
              })
            }
            onRestoreOriginal={() =>
              runMediaAction({
                action: "restoreOriginal",
                task: () => restoreOriginalImage({ id: sponsor._id }),
                successMessage: "Originallogo wurde wiederhergestellt",
                errorMessage: "Originallogo konnte nicht wiederhergestellt werden",
              })
            }
            disabled={isLoading}
            busyAction={isLoading ? "saving" : mediaBusyAction}
            emptyTitle="Logo auswählen"
            previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
          />
        </DialogFormSection>

        <DialogFormSection title="Stammdaten" icon={Building2}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel isOptional={false}>
                    <Building2 className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                    Name des Sponsors
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="z.B. Muster GmbH"
                      {...field}
                      disabled={isLoading}
                      className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assignedUserId"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>
                    <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                    Zuständige Person
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isLoading || users.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11 bg-white border-gray-200 text-left font-normal">
                        <SelectValue placeholder="Niemandem zugewiesen" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE_USER_VALUE}>Niemandem zugewiesen</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user._id} value={user._id}>
                          {user.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>
                    <LinkIcon className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                    Website
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://www.beispiel.de"
                      {...field}
                      disabled={isLoading}
                      className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </DialogFormSection>

        <DialogFormSection title="Adresse" icon={MapPin}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormControl>
                    <Input
                      placeholder="z.B. Alleestraße 12, 12345 Musterstadt"
                      {...field}
                      disabled={isLoading}
                      className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="sm:col-span-2 grid gap-4 sm:grid-cols-[2fr,1fr]">
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Straße"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="streetNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Nr."
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="sm:col-span-2 grid gap-4 sm:grid-cols-[1fr,2fr]">
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="PLZ"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Ort"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormControl>
                    <Input
                      placeholder="Land"
                      {...field}
                      disabled={isLoading}
                      className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </DialogFormSection>

        <DialogFormSection title="Notizen" icon={StickyNote}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormControl>
                    <Textarea
                      placeholder="Interessen, bisherige Kontakte, besondere Vereinbarungen..."
                      {...field}
                      disabled={isLoading}
                      className="min-h-[80px] bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </DialogFormSection>
      </form>
    </Form>
  );
};
